import React, { useCallback } from 'react';
import { Trans } from 'react-i18next';
import { RULE_TYPE_MAP_BY_SLUG } from '@ubeya/shared-web/constants';
import usePositions from '@ubeya/shared/hooks/account/usePositions';
import { SLOT_WARNING_TYPES, BOOKING_USER_PROMPT_CASES } from 'scheduling/constants';

const useBookingPrompt = ({ shift: { positionId } }) => {
  const { mappedPositions } = usePositions();

  const checkUserIsCompliantToPosition = useCallback((userData) => {
    const complianceWarning = (userData?.warnings || [])?.find(
      ({ type }) => type === SLOT_WARNING_TYPES.COMPLIANCE.type
    );

    const { isEmployeeCompliant = true } = complianceWarning?.metadata || {};

    return isEmployeeCompliant;
  }, []);

  // available employees
  const checkEmployeeComplianceWithWTD = useCallback((userData) => {
    const wtdWarning = (userData?.warnings || [])?.find(({ type }) => type === SLOT_WARNING_TYPES.WTD.type);

    const { isEmployeeWTDCompliant = true, employeesNotCompliantToRules = [], isCurrentIssue = false } =
      wtdWarning?.metadata || {};

    return { isEmployeeWTDCompliant, employeesNotCompliantToRules, isCurrentIssue };
  }, []);

  const checkPromptCase = useCallback(
    (user) => {
      if (user.isPending) {
        return { type: BOOKING_USER_PROMPT_CASES.IS_PENDING };
      }

      const { isEmployeeWTDCompliant, employeesNotCompliantToRules, isCurrentIssue } = checkEmployeeComplianceWithWTD(
        user
      );

      const isUserCompliantToPosition = checkUserIsCompliantToPosition(user);

      /* if compliance is blocking, show before other checks */
      if (!isUserCompliantToPosition && mappedPositions[positionId]?.compliance?.blockBooking) {
        return {
          type: BOOKING_USER_PROMPT_CASES.COMPLIANCE_BLOCK
        };
      }

      if (!isEmployeeWTDCompliant) {
        return {
          type: BOOKING_USER_PROMPT_CASES.EXCEED_WORKING_HOURS,
          metaData: employeesNotCompliantToRules,
          isCurrentIssue
        };
      }

      const bookedInSameDay =
        user?.warnings &&
        user?.warnings.length > 0 &&
        user?.warnings.some(({ type }) => type === SLOT_WARNING_TYPES.BOOKED_SAME_DAY.type);

      if (bookedInSameDay) {
        return { type: BOOKING_USER_PROMPT_CASES.BOOKED_SAME_DAY };
      }

      if (positionId && !user?.positions?.includes(positionId)) {
        return {
          type: BOOKING_USER_PROMPT_CASES.POSITION
        };
      }

      if (!isUserCompliantToPosition) {
        return {
          type: BOOKING_USER_PROMPT_CASES.COMPLIANCE
        };
      }

      return { type: null };
    },
    [checkEmployeeComplianceWithWTD, positionId, checkUserIsCompliantToPosition, mappedPositions]
  );

  const getRulePromptInfo = useCallback((ruleData, packageName, user, isCurrentIssue) => {
    const body = (
      <div>
        <Trans
          i18nKey={isCurrentIssue ? 'employeeExceedWorkingHoursBody' : 'employeeExceedWorkingHoursFutureBody'}
          values={{
            period: ruleData.period,
            hours: ruleData.hours,
            name: packageName,
            employeeName: [user?.firstName, user?.lastName]?.filter((val) => !!val)?.join(' ')
          }}
          components={{ bold: <strong /> }}
        />
      </div>
    );

    const blockRule = ruleData?.type === RULE_TYPE_MAP_BY_SLUG.BLOCK;
    const title = blockRule ? 'cantContinue' : 'areYouSure';
    return { body, block: blockRule, title };
  }, []);

  const promptRenderBody = useCallback(
    (user) => {
      let body = '';
      let htmlBody = '';

      const bookingUserPromptCase = checkPromptCase(user);

      switch (bookingUserPromptCase.type) {
        case BOOKING_USER_PROMPT_CASES.COMPLIANCE:
          htmlBody = (
            <div>
              <Trans
                i18nKey="employeeNotCompliantToPositionBody"
                values={{
                  name: `${user.firstName} ${user.lastName}`,
                  position: mappedPositions[positionId]?.label
                }}
                components={{ bold: <strong /> }}
              />
            </div>
          );

          break;

        case BOOKING_USER_PROMPT_CASES.COMPLIANCE_BLOCK:
          htmlBody = (
            <div>
              <Trans
                i18nKey="employeeNotCompliantAndBlockedToPosition"
                values={{
                  name: `${user.firstName} ${user.lastName}`,
                  position: mappedPositions[positionId]?.label
                }}
                components={{ bold: <strong /> }}
              />
            </div>
          );

          break;

        case BOOKING_USER_PROMPT_CASES.EXCEED_WORKING_HOURS: {
          const { body: rulePromptBody } = getRulePromptInfo(
            bookingUserPromptCase.metaData[0].rules[0],
            bookingUserPromptCase.metaData[0].name,
            user,
            bookingUserPromptCase.isCurrentIssue
          );

          if (rulePromptBody) {
            htmlBody = rulePromptBody;
          } else {
            body = 'employeeExceedWorkingHoursBody';
          }

          break;
        }
        case BOOKING_USER_PROMPT_CASES.POSITION:
          body = 'addPositionToEmployee';
          break;

        case BOOKING_USER_PROMPT_CASES.BOOKED_SAME_DAY:
          body = 'thisEmployeeIsAlreadyBookedInAnotherShiftInTheSameDay';
          break;

        case BOOKING_USER_PROMPT_CASES.IS_PENDING:
          body = 'thisEmployeeIsPendingApproval';
          break;

        default:
          body = 'addPositionToEmployee';
      }

      return (
        htmlBody || (
          <div>
            <Trans
              i18nKey={body}
              values={{
                name: `${user.firstName} ${user.lastName}`,
                position: mappedPositions[positionId]?.label
              }}
              components={{ bold: <strong /> }}
            />
          </div>
        )
      );
    },
    [checkPromptCase, getRulePromptInfo, mappedPositions, positionId]
  );

  const showPromptOptions = useCallback(
    (rowData) => {
      const bookingUserPromptCase = checkPromptCase(rowData);

      switch (bookingUserPromptCase.type) {
        case BOOKING_USER_PROMPT_CASES.EXCEED_WORKING_HOURS: {
          const { block } = getRulePromptInfo(bookingUserPromptCase.metaData[0].rules[0]);

          return !block;
        }
        case BOOKING_USER_PROMPT_CASES.COMPLIANCE_BLOCK: {
          return false;
        }

        default:
          return true;
      }
    },
    [checkPromptCase, getRulePromptInfo]
  );

  const promptRenderTitle = useCallback(
    (user) => {
      let title = '';

      const bookingUserPromptCase = checkPromptCase(user);

      switch (bookingUserPromptCase.type) {
        case BOOKING_USER_PROMPT_CASES.COMPLIANCE:
        case BOOKING_USER_PROMPT_CASES.COMPLIANCE_BLOCK:
          title = 'employeeNotCompliantToPositionTitle';
          break;

        case BOOKING_USER_PROMPT_CASES.EXCEED_WORKING_HOURS: {
          const { title: rulePromptTitle } = getRulePromptInfo(bookingUserPromptCase.metaData[0].rules[0]);

          title = rulePromptTitle || 'employeeExceedWorkingHoursTitle';
          break;
        }
        case BOOKING_USER_PROMPT_CASES.POSITION:
          title = 'areYouSure';
          break;

        default:
          title = 'areYouSure';
      }

      return title;
    },
    [checkPromptCase, getRulePromptInfo]
  );

  const promptCondition = useCallback((rowData) => !!checkPromptCase(rowData)?.type, [checkPromptCase]);

  return {
    promptCondition,
    promptRenderBody,
    promptRenderTitle,
    checkPromptCase,
    showPromptOptions,
    checkEmployeeComplianceWithWTD
  };
};

export default useBookingPrompt;
